import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, 
  ArrowRight,
  CheckCircle,
  Download,
  Phone,
  AlertCircle
} from 'lucide-react';

const ConditionsPage = () => {
  const conditions = [
    {
      category: 'Common Illnesses',
      items: [
        'Cold & Flu',
        'Sore Throat',
        'Sinus Infections',
        'Ear Infections',
        'Bronchitis',
        'Upper Respiratory Infections',
        'Strep Throat',
        'Mono',
        'COVID-19 Evaluation'
      ]
    },
    {
      category: 'Skin Conditions',
      items: [
        'Acne',
        'Eczema',
        'Rashes',
        'Poison Ivy/Oak',
        'Bug Bites',
        'Minor Burns',
        'Skin Infections',
        'Hives',
        'Cold Sores'
      ]
    },
    {
      category: 'Allergies',
      items: [
        'Seasonal Allergies',
        'Food Allergies',
        'Allergic Reactions',
        'Hay Fever',
        'Pet Allergies',
        'Dust Allergies'
      ]
    },
    {
      category: 'Digestive Issues',
      items: [
        'Nausea & Vomiting',
        'Diarrhea',
        'Constipation',
        'Acid Reflux',
        'Stomach Pain',
        'Food Poisoning',
        'Heartburn',
        'Indigestion'
      ]
    },
    {
      category: 'Eye & Ear',
      items: [
        'Pink Eye (Conjunctivitis)',
        'Styes',
        'Eye Allergies',
        'Ear Pain',
        'Swimmer\'s Ear',
        'Earwax Buildup'
      ]
    },
    {
      category: 'Urinary Issues',
      items: [
        'Urinary Tract Infections (UTI)',
        'Bladder Infections',
        'Yeast Infections',
        'Bacterial Vaginosis'
      ]
    },
    {
      category: 'Pain & Injuries',
      items: [
        'Headaches',
        'Migraines',
        'Back Pain',
        'Muscle Strains',
        'Joint Pain',
        'Minor Sprains'
      ]
    },
    {
      category: 'Mental Health',
      items: [
        'Anxiety',
        'Depression',
        'Stress Management',
        'Sleep Issues',
        'ADHD Follow-up'
      ]
    },
    {
      category: 'Chronic Condition Management',
      items: [
        'Diabetes Follow-up',
        'Hypertension Follow-up',
        'Asthma Management',
        'Thyroid Conditions',
        'Cholesterol Management'
      ]
    },
    {
      category: 'Preventive Care',
      items: [
        'Medication Refills',
        'Lab Order Reviews',
        'Health Screenings',
        'Prescription Renewals',
        'Second Opinions'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20 pb-12" data-testid="conditions-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-base font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              <Stethoscope className="w-5 h-5" />
              VIRTUAL URGENT CARE
            </div>
            <h1 className="text-4xl lg:text-5xl font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              Conditions We Treat
            </h1>
            <p className="text-white/90 text-xl font-black max-w-3xl mx-auto" style={{ fontFamily: 'Merriweather, serif' }}>
              Our board-certified providers can diagnose and treat a wide range of non-emergency medical conditions via secure video visit.
            </p>
          </div>
        </div>
      </section>

      {/* Emergency Warning */}
      <section className="py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-center gap-4">
            <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-800 text-lg font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                For medical emergencies, call 911 immediately
              </p>
              <p className="text-red-700 text-base font-black">
                Chest pain, difficulty breathing, stroke symptoms, severe bleeding — go to ER
              </p>
            </div>
            <a href="tel:911">
              <button 
                className="bg-red-600 hover:bg-red-700 text-white font-black px-6 py-3 rounded-lg"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                <Phone className="w-4 h-4 inline mr-2" />
                Call 911
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Conditions Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {conditions.map((category, index) => (
              <div 
                key={index}
                className="bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A]"
              >
                <h3 className="text-xl font-black text-[#1E3A8A] mb-4 pb-2 border-b-2 border-[#1E3A8A]" style={{ fontFamily: 'Merriweather, serif' }}>
                  {category.category}
                </h3>
                <ul className="space-y-2">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-base font-black text-slate-800" style={{ fontFamily: 'Merriweather, serif' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Ready to See a Provider?
          </h2>
          <p className="text-white/90 text-xl font-black mb-8" style={{ fontFamily: 'Merriweather, serif' }}>
            Download the Spruce Health app to get started with your virtual visit today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://spruce.care/integratedwellness" target="_blank" rel="noopener noreferrer">
              <button 
                className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] text-xl font-black px-8 py-4 rounded-lg transition-all shadow-lg hover:bg-white/90"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                <Download className="w-6 h-6" />
                Download Spruce Health App
              </button>
            </a>
            <Link to="/chatbot">
              <button 
                className="inline-block bg-white/20 border-2 border-white text-white text-xl font-black px-8 py-4 rounded-lg transition-all hover:bg-white/30"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                Chat with AI First
                <ArrowRight className="w-5 h-5 inline ml-2" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-700 text-base font-black" style={{ fontFamily: 'Merriweather, serif' }}>
            This list is not exhaustive. If you're unsure whether we can treat your condition, 
            download the Spruce Health app and message us or chat with our AI assistant.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ConditionsPage;
